import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Globe2,
  Users,
  TrendingUp,
  MessageCircle,
  Calendar,
  AlertTriangle,
  Send,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Search,
  Loader2,
  Database,
  Languages,
  ExternalLink,
  MessagesSquare,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// =============================================================================
// Marketing Analytics → Landings & Leads
// =============================================================================
// Split-view: KPI strip + TR vs UA comparison + per-landing top UTMs / geos +
// daily trend at the top, full paginated submissions table at the bottom.
//
// Data source: db.landing_leads (mirror of chat.log NDJSON pulled every 5 min
// from spinhouse-landings/logs-api.php). Independent of the CRM mirror — shows
// EVERY form submission / booking / chat session / OpenAI error from the live
// landing pages, including ones that never made it into the CRM.
//
// Backend endpoints:
//   GET /landing-leads/sync/status         — sync state, counts in DB
//   GET /landing-leads/overview            — per-landing KPI + funnel + top lists
//   GET /landing-leads/submissions         — paginated raw rows with filters
//   POST /landing-leads/sync/incremental   — manual trigger (write-roles)
// =============================================================================

function calendarMonthRange(anchorMonthsAgo = 0) {
  const now = new Date();
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - anchorMonthsAgo, 1));
  const first = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), 1));
  let last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
  if (anchorMonthsAgo === 0 && last > now) last = new Date(now);
  const label = first.toLocaleString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10), label };
}

const BUCKET_META = {
  form_lead: { label: "Form lead",    icon: Send,           color: "text-emerald-300", badge: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" },
  chat_lead: { label: "Chat lead",    icon: MessageCircle,  color: "text-emerald-300", badge: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" },
  booking:   { label: "Booking",      icon: Calendar,       color: "text-cyan-300",    badge: "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" },
  chat:      { label: "Chat msg",     icon: MessageCircle,  color: "text-blue-300",    badge: "bg-blue-500/15 border-blue-500/40 text-blue-200" },
  error:     { label: "Error",        icon: AlertTriangle,  color: "text-red-300",     badge: "bg-red-500/15 border-red-500/40 text-red-200" },
  other:     { label: "Other",        icon: Database,       color: "text-zinc-300",    badge: "bg-zinc-700/30 border-zinc-700 text-zinc-300" },
};

// Every landing that writes to the shared logs API (backend LANDING_CODES).
// The page used to know only TR and UA, which hid KZ — the second biggest.
const LANDING_META = {
  tr: { label: "TR landing", domain: "landing.spinhouse.ai",    flag: "🇹🇷" },
  ua: { label: "UA landing", domain: "ua.spinhouse.ai",         flag: "🇺🇦" },
  kz: { label: "KZ landing", domain: "kz.spinhouse.ai",         flag: "🇰🇿" },
  uz: { label: "UZ landing", domain: "uz.spinhouse.ai",         flag: "🇺🇿" },
  en: { label: "EN landing", domain: "landing.spinhouse.ai/en", flag: "🌐" },
};
const LANDING_CODES = Object.keys(LANDING_META);

function fmtNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}

function fmtDate(s) {
  if (!s) return "—";
  // Accept both "YYYY-MM-DD HH:MM:SS" and ISO. Convert to local for display.
  const tryParse = (val) => {
    const d = new Date(val.replace(" ", "T") + (val.endsWith("Z") ? "" : "Z"));
    return isNaN(d) ? null : d;
  };
  const d = tryParse(s) || tryParse(s + ":00") || null;
  if (!d) return s;
  // 4-digit year — `26` was being misread as a day-of-month next to "May 26".
  // If same year as today, drop the year part for compactness.
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncate(s, n = 60) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export default function MarketingLandingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialMonth = 0;
  const initial = useMemo(() => {
    const r = calendarMonthRange(initialMonth);
    return {
      from: searchParams.get("from") || r.from,
      to: searchParams.get("to") || r.to,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [monthAnchor, setMonthAnchor] = useState(initialMonth);

  // Filters
  const [landingFilter, setLandingFilter] = useState("all"); // all|tr|ua
  const [bucketFilter, setBucketFilter] = useState(null);    // null|form_lead|booking|chat|error
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Pagination
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Data
  const [overview, setOverview] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [syncState, setSyncState] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loadingOv, setLoadingOv] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  // Audit drilldown table filter — null = hide table, value = show with filter
  const [auditFilter, setAuditFilter] = useState(null);

  // ---- Fetchers ----
  const fetchOverview = async () => {
    setLoadingOv(true);
    try {
      const res = await api.get("/landing-leads/overview", {
        params: { date_from: dateFrom, date_to: dateTo, landing: "all" },
      });
      setOverview(res.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoadingOv(false);
    }
  };

  const fetchSubmissions = async () => {
    setLoadingSubs(true);
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        landing: landingFilter,
        offset,
        limit,
      };
      if (bucketFilter) params.bucket = bucketFilter;
      if (searchText.trim()) params.q = searchText.trim();
      const res = await api.get("/landing-leads/submissions", { params });
      setSubmissions(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoadingSubs(false);
    }
  };

  const fetchAudit = async () => {
    setLoadingAudit(true);
    try {
      const res = await api.get("/landing-leads/audit", {
        params: { date_from: dateFrom, date_to: dateTo, landing: "all" },
      });
      setAudit(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoadingAudit(false);
    }
  };

  const fetchSyncState = async () => {
    try {
      const res = await api.get("/landing-leads/sync/status");
      setSyncState(res.data);
    } catch (_) { /* ignore — UI degrades cleanly */ }
  };

  const triggerSync = async () => {
    setTriggering(true);
    try {
      await api.post("/landing-leads/sync/incremental");
      await Promise.all([fetchOverview(), fetchSubmissions(), fetchSyncState()]);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchAudit();
    fetchSyncState();
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOffset(0);
  }, [landingFilter, bucketFilter, searchText, dateFrom, dateTo]);

  useEffect(() => {
    fetchSubmissions();
  }, [dateFrom, dateTo, landingFilter, bucketFilter, searchText, offset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchParams({ from: dateFrom, to: dateTo }, { replace: true });
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = overview?.totals || {};
  const landings = overview?.landings || {};

  // Daily trend — one series summed over every landing.
  const dailyMerged = useMemo(() => {
    const map = {};
    LANDING_CODES.forEach((code) => {
      ((landings[code] || {}).daily_trend || []).forEach((r) => {
        const d = map[r.date] || (map[r.date] = { date: r.date, form_leads: 0, chat_leads: 0, bookings: 0, chat: 0, errors: 0 });
        d.form_leads += r.form_leads || 0;
        d.chat_leads += r.chat_leads || 0;
        d.bookings += r.bookings || 0;
        d.chat += r.chat || 0;
        d.errors += r.errors || 0;
      });
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [landings]);

  const dayLeadCount = (d) => d.form_leads + d.chat_leads + d.bookings;
  const dayTotalCount = (d) => dayLeadCount(d) + d.chat;

  const maxDailyTotal = useMemo(() => {
    if (!dailyMerged.length) return 0;
    return Math.max(...dailyMerged.map(dayTotalCount));
  }, [dailyMerged]);

  // Map audit entries by _dedup_key so we can show per-row CRM match
  // status + deal stage right inside the submissions table. Replaces the
  // standalone /landings/diagnostic page (deleted 2026-05-27 — info was
  // duplicated, one table is cleaner).
  const auditByKey = useMemo(() => {
    const m = {};
    (audit?.entries || []).forEach((a) => {
      if (a._dedup_key) m[a._dedup_key] = a;
    });
    return m;
  }, [audit]);

  // Visual meta for CRM match status — kept here so submissions table
  // and audit summary cards stay in sync.
  const AUDIT_STATUS_META = {
    tagged_landing:         { label: "✓ Tagged",       cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" },
    missing_landing_marker: { label: "⚠ Untagged",     cls: "bg-amber-500/15 border-amber-500/40 text-amber-200" },
    saved_no_deal:          { label: "⚠ No deal",      cls: "bg-amber-500/15 border-amber-500/40 text-amber-200" },
    missing_in_crm:     { label: "✗ Missing",      cls: "bg-red-500/15 border-red-500/40 text-red-200" },
    // Reached the CRM and was deleted there. Reporting this as a delivery
    // failure blamed the landing for someone else's cleanup.
    deleted_in_crm:     { label: "🗑 Deleted in CRM", cls: "bg-zinc-700/30 border-zinc-600 text-zinc-300" },
  };

  const stageCls = (status, prob) => {
    if (status === "won") return "bg-emerald-500/15 border-emerald-500/40 text-emerald-200";
    if (status === "lost") return "bg-red-500/15 border-red-500/40 text-red-200";
    if ((prob || 0) >= 0.5) return "bg-cyan-500/15 border-cyan-500/40 text-cyan-200";
    if ((prob || 0) >= 0.3) return "bg-amber-500/15 border-amber-500/40 text-amber-200";
    return "bg-zinc-800 border-zinc-700 text-zinc-300";
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchText(searchInput);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Tab nav — mirror MarketingAnalyticsPage tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800 pb-0.5">
        <Link to="/marketing-analytics" className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition">Overview</Link>
        <Link to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> People
        </Link>
        <Link to={`/marketing-analytics/sales?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Sales scoring
        </Link>
        <Link to={`/marketing-analytics/landings?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-blue-400 font-medium border-b-2 border-blue-500 -mb-0.5 inline-flex items-center gap-1">
          <Globe2 className="w-3 h-3" /> Landings & Leads
        </Link>
        <Link to={`/marketing-analytics/chats?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <MessagesSquare className="w-3 h-3" /> Chats
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Globe2 className="w-6 h-6 text-blue-400" /> Landings &amp; Leads
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            All form submissions, bookings, chat sessions and errors straight from the Spinhouse landings ({LANDING_CODES.map((c) => c.toUpperCase()).join(" · ")}) — independent of the CRM. Useful to spot leads that never reached the CRM.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={triggering}
            onClick={triggerSync}
            className="border-zinc-700 hover:bg-zinc-800 h-8">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${triggering ? "animate-spin" : ""}`} />
            Sync now
          </Button>
        </div>
      </div>

      {/* Sync state banner */}
      {syncState && (
        <div className="text-xs text-zinc-500 bg-zinc-900/40 border border-zinc-800 rounded px-3 py-1.5 flex flex-wrap gap-3 items-center">
          {syncState.configured ? (
            <span className="text-emerald-400">● Configured</span>
          ) : (
            <span className="text-amber-400">● Not configured (set LANDING_LOGS_API_KEY on backend)</span>
          )}
          {syncState.last_incremental_at && (
            <span>Last sync: <span className="text-zinc-300">{fmtDate(syncState.last_incremental_at)}</span></span>
          )}
          {syncState.counts_in_db && (
            <span>In DB: <span className="text-zinc-300">{fmtNum(syncState.counts_in_db.total)}</span> rows ({LANDING_CODES.map((c) => `${c.toUpperCase()} ${fmtNum(syncState.counts_in_db[c])}`).join(" · ")})</span>
          )}
          {syncState.last_summary?.errors?.length > 0 && (
            <span className="text-red-400">⚠ {syncState.last_summary.errors.length} sync errors</span>
          )}
        </div>
      )}

      {/* Period picker — explicit quick ranges + dropdown for any past month + custom dates.
          NB: rolling ranges (Last 7/30/90/365d) and month-specific picks are mutually exclusive. */}
      <div className="flex items-center gap-2 flex-wrap text-xs bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
        <span className="text-zinc-500 uppercase tracking-wider">Period</span>

        {/* Rolling windows */}
        {[
          { days: 7,   label: "7d",   key: "r7"   },
          { days: 30,  label: "30d",  key: "r30"  },
          { days: 90,  label: "90d",  key: "r90"  },
          { days: 365, label: "365d", key: "r365" },
        ].map((p) => {
          const to = new Date();
          const from = new Date();
          from.setDate(to.getDate() - p.days + 1);
          const fromIso = from.toISOString().slice(0, 10);
          const toIso = to.toISOString().slice(0, 10);
          const active = dateFrom === fromIso && dateTo === toIso;
          return (
            <button key={p.key} type="button"
              onClick={() => { setMonthAnchor(null); setDateFrom(fromIso); setDateTo(toIso); }}
              className={`px-2.5 py-1 rounded border transition ${active ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"}`}>
              Last {p.label}
            </button>
          );
        })}

        <span className="text-zinc-700 mx-1">·</span>

        {/* Month-specific quick picks */}
        {[
          { months: 0, label: "This month" },
          { months: 1, label: "Last month" },
        ].map((p) => {
          const r = calendarMonthRange(p.months);
          const active = monthAnchor === p.months;
          return (
            <button key={p.label} type="button"
              onClick={() => { setMonthAnchor(p.months); setDateFrom(r.from); setDateTo(r.to); }}
              className={`px-2.5 py-1 rounded border transition ${active ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"}`}>
              {p.label}
            </button>
          );
        })}

        {/* Any month dropdown — last 18 months */}
        <select
          value={monthAnchor != null && monthAnchor >= 0 ? String(monthAnchor) : ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return;
            const months = parseInt(v, 10);
            const r = calendarMonthRange(months);
            setMonthAnchor(months);
            setDateFrom(r.from);
            setDateTo(r.to);
          }}
          className="bg-zinc-900/60 border border-zinc-800 rounded px-2 py-1 text-zinc-300 hover:border-zinc-700 cursor-pointer">
          <option value="">Any month…</option>
          {Array.from({ length: 18 }).map((_, i) => {
            const r = calendarMonthRange(i);
            return <option key={i} value={String(i)}>{r.label}</option>;
          })}
        </select>

        <span className="text-zinc-700 mx-1">·</span>

        {/* Custom date range */}
        <span className="text-zinc-500">Custom</span>
        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setMonthAnchor(null); }}
          className="bg-zinc-900/60 border border-zinc-800 rounded px-2 py-1 text-zinc-300" />
        <span className="text-zinc-600">→</span>
        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setMonthAnchor(null); }}
          className="bg-zinc-900/60 border border-zinc-800 rounded px-2 py-1 text-zinc-300" />
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* ========== KPI strip (totals across both landings) ==========
          "Total leads" = form_leads + bookings + chat_lead_sessions
          (chat sessions where visitor actually gave a phone/email).
          Plain "Chat sessions" stays as a separate engagement metric. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { key: "total_leads",   label: "Total leads",   color: "text-emerald-300", hint: "Forms + bookings + chats with contact" },
          { key: "form_leads",    label: "Form leads",    color: "text-emerald-200" },
          { key: "bookings",      label: "Bookings",      color: "text-cyan-300" },
          { key: "chat_leads",    label: "Chat leads",    color: "text-emerald-200", hint: "Chat sessions where phone or email was given" },
          { key: "chat_sessions", label: "Chat sessions", color: "text-blue-300",    hint: "All chat conversations (incl. without contact)" },
          { key: "errors",        label: "AI errors",     color: "text-red-300" },
        ].map((k) => (
          <Card key={k.key} className="bg-zinc-900/40 border-zinc-800" title={k.hint || ""}>
            <CardContent className="p-4">
              <div className={`text-3xl font-bold ${k.color}`}>{fmtNum(totals[k.key])}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{k.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ========== One card per landing ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="landing-cards">
        {LANDING_CODES.map((code) => {
          const ln = landings[code] || {};
          const meta = LANDING_META[code];
          return (
            <Card key={code} className="bg-zinc-900/40 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  <span className="flex items-center gap-2 text-zinc-100">
                    <span className="text-xl">{meta.flag}</span>
                    {meta.label}
                  </span>
                  <a href={`https://${meta.domain}`} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1">
                    {meta.domain} <ExternalLink className="w-3 h-3" />
                  </a>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { key: "form_leads",         label: "Forms",      cls: "text-emerald-300" },
                    { key: "bookings",           label: "Bookings",   cls: "text-cyan-300" },
                    { key: "chat_lead_sessions", label: "Chat leads", cls: "text-emerald-200", hint: "Chat sessions with contact" },
                    { key: "chat_sessions",      label: "Chats",      cls: "text-blue-300",    hint: "All chat sessions" },
                    { key: "errors",             label: "Errors",     cls: "text-red-300" },
                  ].map((m) => (
                    <div key={m.key} className="bg-zinc-950/40 border border-zinc-800 rounded px-2 py-1.5 text-center" title={m.hint || ""}>
                      <div className={`text-lg font-semibold ${m.cls}`}>{fmtNum(ln[m.key])}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{m.label}</div>
                    </div>
                  ))}
                </div>

                {ln.top_utm_campaigns?.length > 0 && (
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Top campaigns</div>
                    <div className="space-y-1">
                      {ln.top_utm_campaigns.slice(0, 6).map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-zinc-300 truncate mr-2" title={c.name}>{c.name || "(empty)"}</span>
                          <span className="text-zinc-500 font-mono">{fmtNum(c.count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {ln.top_geos?.length > 0 && (
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Top geos</div>
                    <div className="flex flex-wrap gap-1.5">
                      {ln.top_geos.slice(0, 6).map((g, i) => (
                        <span key={i} className="text-[11px] bg-zinc-800/60 border border-zinc-700 rounded px-2 py-0.5 text-zinc-300">
                          {g.name} <span className="text-zinc-500">{g.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ========== Daily trend (stacked bars, colored per bucket) ========== */}
      {dailyMerged.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center justify-between flex-wrap gap-3">
              <span>Daily activity (all landings stacked)</span>
              <div className="flex items-center gap-3 text-[11px] font-normal text-zinc-400">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Form leads</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-300" /> Chat leads</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-cyan-500" /> Bookings</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500/70" /> Chat (no contact)</span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-0.5 h-40">
              {dailyMerged.map((d) => {
                const segments = [
                  { n: d.form_leads, cls: "bg-emerald-500" },
                  { n: d.chat_leads, cls: "bg-emerald-300" },
                  { n: d.bookings,   cls: "bg-cyan-500" },
                  { n: d.chat,       cls: "bg-blue-500/70" },
                ];
                const total = segments.reduce((a, s) => a + s.n, 0);
                const ratio = maxDailyTotal > 0 ? total / maxDailyTotal : 0;
                const heightPct = total === 0 ? 0 : Math.max(4, ratio * 100);
                const tooltip = `${d.date}: ${total} events · ${dayLeadCount(d)} leads (forms ${d.form_leads}, chat-leads ${d.chat_leads}, bookings ${d.bookings}) + ${d.chat} chat-no-contact`;
                return (
                  <div key={d.date} className="flex-1 min-w-0 flex flex-col justify-end" title={tooltip}>
                    <div className="w-full flex flex-col-reverse rounded-t overflow-hidden hover:brightness-125 transition" style={{ height: `${heightPct}%` }}>
                      {segments.filter((s) => s.n > 0).map((s, i) => (
                        <div key={i} className={`${s.cls} w-full`} style={{ flex: `${s.n} 0 0%`, minHeight: 1 }} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-zinc-500 mt-1.5">
              <span>{dailyMerged[0]?.date}</span>
              <span className="text-zinc-600">{dailyMerged.length} days · max {maxDailyTotal}/day</span>
              <span>{dailyMerged[dailyMerged.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== CRM audit ==========
          Cross-check landing_leads (source of truth here) against the CRM
          mirror. For every form_lead/booking/chat_lead in the date range:
          - tagged_landing       → ✓ saved + Landing Lead deal exists
          - missing_landing_marker → ⚠ saved but deal renamed/manual
          - saved_no_deal        → ⚠ contact exists but deal creation failed
          - missing_in_crm   → ✗ never reached the CRM (push failed)
          - deleted_in_crm   → 🗑 reached the CRM, then deleted there
      */}
      {audit && audit.summary && (
        <Card className="bg-zinc-900/40 border-zinc-800 border-l-4 border-l-cyan-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              Delivery audit — landing leads → CRM match
              {loadingAudit && <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-zinc-400 leading-relaxed">
              For every form lead / booking / chat lead in <span className="text-zinc-200">landing_leads</span> we check
              if a matching CRM contact exists (by email or last-9-digit phone) and whether its deal has the
              <code className="text-emerald-300 mx-1">Landing Lead — … [FORM_LEAD]</code>
              marker. Click a card to drill into that group.
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
              {[
                { key: "total", label: "Total leads", n: audit.summary.total, color: "zinc", desc: "form_lead + chat_lead + booking" },
                { key: "tagged_landing", label: "Tagged ✓", n: audit.summary.tagged_landing, color: "emerald", desc: "Saved with Landing Lead deal" },
                { key: "missing_landing_marker", label: "Untagged ⚠", n: audit.summary.missing_landing_marker, color: "amber", desc: "Saved, deal renamed/manual" },
                { key: "saved_no_deal", label: "Contact only ⚠", n: audit.summary.saved_no_deal, color: "amber", desc: "No deal attached" },
                { key: "missing_in_crm", label: "Missing ✗", n: audit.summary.missing_in_crm, color: "red", desc: "Never reached the CRM" },
                { key: "deleted_in_crm", label: "Deleted 🗑", n: audit.summary.deleted_in_crm, color: "zinc", desc: "Arrived, then deleted in the CRM" },
              ].map((c) => {
                const palette = {
                  zinc: "bg-zinc-800/40 border-zinc-700 text-zinc-200",
                  emerald: "bg-emerald-500/10 border-emerald-500/40 text-emerald-100",
                  amber: "bg-amber-500/10 border-amber-500/40 text-amber-100",
                  red: "bg-red-500/10 border-red-500/40 text-red-100",
                }[c.color];
                const active = auditFilter === c.key;
                const clickable = c.key !== "total" || true; // total opens unfiltered table
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => clickable && setAuditFilter(active ? null : c.key)}
                    className={`border rounded-lg p-2.5 text-left transition ${palette} ${active ? "ring-2 ring-blue-400/60" : "hover:opacity-90"}`}
                    title={c.desc}
                  >
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{c.label}</div>
                    <div className="text-2xl font-semibold tabular-nums mt-0.5">{fmtNum(c.n)}</div>
                    <div className="text-[10px] opacity-60 mt-1 truncate">{c.desc}</div>
                  </button>
                );
              })}
            </div>

            {/* Per-landing + per-bucket sub-breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-zinc-800 rounded-lg bg-zinc-950/40 p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Per landing</div>
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="text-left pb-1">Landing</th>
                      <th className="text-right pb-1">Total</th>
                      <th className="text-right pb-1 text-emerald-400">Tagged</th>
                      <th className="text-right pb-1 text-red-400">Missing</th>
                      <th className="text-right pb-1 text-amber-400">No deal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {Object.entries(audit.per_landing || {}).map(([ln, st]) => (
                      <tr key={ln}>
                        <td className="py-1 text-zinc-200">{LANDING_META[ln]?.flag} {LANDING_META[ln]?.label || ln}</td>
                        <td className="py-1 text-right tabular-nums text-zinc-300">{fmtNum(st.total)}</td>
                        <td className="py-1 text-right tabular-nums text-emerald-300">{fmtNum(st.tagged_landing)}</td>
                        <td className="py-1 text-right tabular-nums text-red-300">{fmtNum(st.missing_in_crm)}</td>
                        <td className="py-1 text-right tabular-nums text-amber-300">{fmtNum(st.saved_no_deal + st.missing_landing_marker)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-zinc-800 rounded-lg bg-zinc-950/40 p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Per bucket</div>
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="text-left pb-1">Bucket</th>
                      <th className="text-right pb-1">Total</th>
                      <th className="text-right pb-1 text-emerald-400">Tagged</th>
                      <th className="text-right pb-1 text-red-400">Missing</th>
                      <th className="text-right pb-1 text-amber-400">Other ⚠</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {Object.entries(audit.per_bucket || {}).map(([b, st]) => (
                      <tr key={b}>
                        <td className="py-1 text-zinc-200">{BUCKET_META[b]?.label || b}</td>
                        <td className="py-1 text-right tabular-nums text-zinc-300">{fmtNum(st.total)}</td>
                        <td className="py-1 text-right tabular-nums text-emerald-300">{fmtNum(st.tagged_landing)}</td>
                        <td className="py-1 text-right tabular-nums text-red-300">{fmtNum(st.missing_in_crm)}</td>
                        <td className="py-1 text-right tabular-nums text-amber-300">{fmtNum(st.saved_no_deal + st.missing_landing_marker)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Drill-down table — visible when a status card is clicked */}
            {auditFilter && (() => {
              const rows = (audit.entries || []).filter((r) => auditFilter === "total" || r.status === auditFilter);
              const statusMeta = {
                tagged_landing: { label: "✓ Saved & tagged", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" },
                missing_landing_marker: { label: "⚠ Saved, untagged", cls: "bg-amber-500/15 border-amber-500/40 text-amber-200" },
                saved_no_deal: { label: "⚠ Contact only", cls: "bg-amber-500/15 border-amber-500/40 text-amber-200" },
                missing_in_crm: { label: "✗ Missing", cls: "bg-red-500/15 border-red-500/40 text-red-200" },
                deleted_in_crm: { label: "🗑 Deleted in CRM", cls: "bg-zinc-700/30 border-zinc-600 text-zinc-300" },
              };
              return (
                <div className="border border-zinc-800 rounded-lg overflow-hidden">
                  <div className="bg-zinc-900/60 px-3 py-1.5 text-[11px] flex items-center justify-between">
                    <span className="text-zinc-400">
                      {rows.length} {rows.length === 1 ? "lead" : "leads"} in <span className="text-zinc-200">{auditFilter}</span>
                    </span>
                    <button type="button" onClick={() => setAuditFilter(null)} className="text-zinc-500 hover:text-zinc-200">
                      Close ✕
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1.5">Status</th>
                          <th className="text-left px-2 py-1.5">Landing</th>
                          <th className="text-left px-2 py-1.5">Bucket</th>
                          <th className="text-left px-2 py-1.5">Time</th>
                          <th className="text-left px-2 py-1.5">Name</th>
                          <th className="text-left px-2 py-1.5">Email</th>
                          <th className="text-left px-2 py-1.5">Phone</th>
                          <th className="text-left px-2 py-1.5">CRM details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800">
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center text-zinc-500 italic py-4">No leads in this category.</td>
                          </tr>
                        ) : rows.map((r) => {
                          const meta = statusMeta[r.status] || statusMeta.missing_in_crm;
                          return (
                            <tr key={r._dedup_key} className="hover:bg-zinc-900/30">
                              <td className="px-2 py-1.5 whitespace-nowrap">
                                <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] ${meta.cls}`}>{meta.label}</span>
                              </td>
                              <td className="px-2 py-1.5 text-zinc-300">{LANDING_META[r._landing]?.flag} {r._landing?.toUpperCase()}</td>
                              <td className="px-2 py-1.5 text-zinc-400">{r.bucket}</td>
                              <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap font-mono">{r.time?.slice(0,16) || "—"}</td>
                              <td className="px-2 py-1.5 text-zinc-200 truncate max-w-[160px]">{r.extracted_name || "—"}</td>
                              <td className="px-2 py-1.5 text-zinc-300 truncate max-w-[180px]">{r.extracted_email || <span className="italic text-zinc-600">—</span>}</td>
                              <td className="px-2 py-1.5 text-zinc-400 font-mono whitespace-nowrap">{r.extracted_phone || "—"}</td>
                              <td className="px-2 py-1.5 text-zinc-300 max-w-[260px]">
                                {r.contact_crm_id ? (
                                  <>
                                    <div className="truncate">{r.contact_name || "(no name)"}</div>
                                    {r.sample_dealname && (
                                      <div className="text-[10px] text-zinc-500 truncate" title={r.sample_dealname}>deal: {r.sample_dealname}</div>
                                    )}
                                  </>
                                ) : (
                                  <span className="italic text-red-300/80">not found in the CRM</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ========== Submissions table ========== */}
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-zinc-100 flex items-center justify-between">
            <span>Submissions</span>
            <span className="text-xs text-zinc-500 font-normal">{fmtNum(submissions?.total)} matching</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-zinc-500 uppercase tracking-wider">Landing</span>
            {["all", ...LANDING_CODES].map((ln) => (
              <button key={ln} type="button" onClick={() => setLandingFilter(ln)}
                className={`px-2.5 py-1 rounded border transition ${landingFilter === ln ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"}`}>
                {ln === "all" ? "All" : `${LANDING_META[ln].flag} ${ln.toUpperCase()}`}
              </button>
            ))}
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-zinc-500 uppercase tracking-wider">Type</span>
            <button type="button" onClick={() => setBucketFilter(null)}
              className={`px-2.5 py-1 rounded border transition ${!bucketFilter ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"}`}>
              All
            </button>
            {["form_lead", "chat_lead", "booking", "chat", "error"].map((b) => {
              const m = BUCKET_META[b];
              const Icon = m.icon;
              return (
                <button key={b} type="button" onClick={() => setBucketFilter(b === bucketFilter ? null : b)}
                  className={`px-2.5 py-1 rounded border transition inline-flex items-center gap-1 ${bucketFilter === b ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"}`}>
                  <Icon className="w-3 h-3" /> {m.label}
                </button>
              );
            })}
            <form onSubmit={handleSearch} className="ml-auto flex items-center gap-1">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search name / phone / email / campaign"
                  className="h-7 pl-7 pr-2 text-xs w-64 bg-zinc-900/60 border-zinc-800 text-zinc-200" />
              </div>
              <Button type="submit" size="sm" variant="outline" className="h-7 px-2 border-zinc-700 text-xs">Search</Button>
              {searchText && (
                <Button type="button" size="sm" variant="ghost" onClick={() => { setSearchInput(""); setSearchText(""); }}
                  className="h-7 px-2 text-xs text-zinc-500">Clear</Button>
              )}
            </form>
          </div>

          {/* Table */}
          <div className="border border-zinc-800 rounded overflow-hidden">
            <div className="grid grid-cols-[20px_130px_50px_110px_minmax(200px,1.4fr)_100px_140px_minmax(120px,0.9fr)_minmax(90px,0.7fr)] gap-2 px-3 py-2 bg-zinc-950/40 border-b border-zinc-800 text-[11px] uppercase tracking-wider text-zinc-500">
              <span></span>
              <span>Time</span>
              <span>Landing</span>
              <span>Type</span>
              <span>Name / contact</span>
              <span>CRM</span>
              <span>Stage</span>
              <span>Campaign</span>
              <span>Source</span>
            </div>

            {loadingSubs && (
              <div className="p-6 text-center text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </div>
            )}

            {!loadingSubs && (!submissions?.items?.length) && (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No submissions match the current filters.
              </div>
            )}

            {!loadingSubs && submissions?.items?.map((row) => {
              const isOpen = expanded.has(row._dedup_key);
              const meta = BUCKET_META[row.bucket] || BUCKET_META.other;
              const Icon = meta.icon;
              const contact = row.extracted_name || row.extracted_phone || row.extracted_email || "—";
              const subContact = [row.extracted_phone, row.extracted_email].filter(Boolean).join(" · ");
              return (
                <div key={row._dedup_key} className="border-b border-zinc-800 last:border-b-0">
                  <button type="button" onClick={() => toggleExpand(row._dedup_key)}
                    className="w-full grid grid-cols-[20px_130px_50px_110px_minmax(200px,1.4fr)_100px_140px_minmax(120px,0.9fr)_minmax(90px,0.7fr)] gap-2 px-3 py-2 text-xs hover:bg-zinc-800/30 text-left items-center">
                    <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                    <span className="text-zinc-300 font-mono text-[11px]">{fmtDate(row.time)}</span>
                    <span className="text-base">{LANDING_META[row._landing]?.flag || "·"}</span>
                    <span title={row.type}
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border min-w-0 max-w-full ${meta.badge}`}>
                      <Icon className="w-3 h-3 shrink-0" />
                      <span className="truncate">{meta.label}</span>
                    </span>
                    <span className="text-zinc-200 truncate">
                      {contact !== "—" ? <span className="font-medium">{contact}</span> : <span className="text-zinc-600">no contact</span>}
                      {subContact && <span className="text-zinc-500 ml-1">· {subContact}</span>}
                    </span>
                    {/* CRM match column — only meaningful for contact-bucket
                        types (form_lead / chat_lead / booking). For chat msgs
                        and errors we show "—" since there's nothing to match. */}
                    {(() => {
                      const a = auditByKey[row._dedup_key];
                      if (!a) {
                        // No audit entry — chat msg / error / non-contact bucket
                        return <span className="text-zinc-700 text-[10px]">—</span>;
                      }
                      const meta = AUDIT_STATUS_META[a.status] || AUDIT_STATUS_META.missing_in_crm;
                      return (
                        <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] truncate ${meta.cls}`} title={a.contact_name || ""}>
                          {meta.label}
                        </span>
                      );
                    })()}
                    {/* Deal stage column — color-coded by CRM win probability */}
                    {(() => {
                      const a = auditByKey[row._dedup_key];
                      const deal = a?.deal;
                      if (!deal) return <span className="text-zinc-700 text-[10px]">—</span>;
                      return (
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] truncate ${stageCls(deal.stage_status, deal.stage_probability)}`}
                          title={`probability ${Math.round((deal.stage_probability || 0) * 100)}%`}
                        >
                          {deal.stage_label || "—"}
                        </span>
                      );
                    })()}
                    <span className="text-zinc-300 truncate" title={row.utm_campaign}>{row.utm_campaign || <span className="text-zinc-600">—</span>}</span>
                    <span className="text-zinc-400 truncate">{row.ad_source || row.utm_source || <span className="text-zinc-600">—</span>}</span>
                  </button>
                  {isOpen && (() => {
                    const a = auditByKey[row._dedup_key];
                    const deal = a?.deal;
                    return (
                    <div className="px-3 py-3 bg-zinc-950/30 border-t border-zinc-800/50 grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
                      {/* CRM match — new column, replaces standalone
                          /landings/diagnostic page deleted 2026-05-27. */}
                      <div>
                        <div className="text-zinc-500 uppercase tracking-wider mb-1">CRM match</div>
                        {!a ? (
                          <div className="text-zinc-600 italic">Not audited (chat msg / error / non-contact bucket).</div>
                        ) : !a.contact_crm_id ? (
                          <div className="text-red-300/80">
                            <div>Not found in the CRM mirror.</div>
                            <div className="text-zinc-500 text-[10px] mt-1 leading-snug">
                              Either the landing → CRM push failed, or sync hasn't run yet, or this is a
                              CHAT_LEAD type (chat.php doesn't push those).
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-[100px_1fr] gap-y-0.5 text-[11px]">
                            <span className="text-zinc-500">Contact</span>
                            <span className="text-zinc-200 font-medium">{a.contact_name || "(no name)"}</span>
                            {a.contact_email && (<><span className="text-zinc-500">Email on file</span><span className="text-zinc-300">{a.contact_email}</span></>)}
                            {a.crm_source && (<><span className="text-zinc-500">hs_source</span><span className="text-zinc-300">{a.crm_source}</span></>)}
                            {a.utm_campaign && (<><span className="text-zinc-500">utm_campaign</span><span className="text-zinc-300">{a.utm_campaign}</span></>)}
                            {deal && (
                              <>
                                <span className="text-zinc-500 pt-1 border-t border-zinc-800 mt-0.5">Deal</span>
                                <span className="text-zinc-200 pt-1 border-t border-zinc-800 mt-0.5">{deal.name || "(unnamed)"}</span>
                                <span className="text-zinc-500">Stage</span>
                                <span><span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] ${stageCls(deal.stage_status, deal.stage_probability)}`}>{deal.stage_label}</span> <span className="text-zinc-500 ml-1">{Math.round((deal.stage_probability || 0) * 100)}%</span></span>
                                {deal.amount > 0 && (<><span className="text-zinc-500">Amount</span><span className="text-emerald-300 font-mono">{deal.currency === "USD" ? "$" : deal.currency === "EUR" ? "€" : deal.currency === "UAH" ? "₴" : ""}{Math.round(deal.amount).toLocaleString()}</span></>)}
                                {deal.owner_name && (<><span className="text-zinc-500">Owner</span><span className="text-zinc-300">{deal.owner_name}</span></>)}
                                <span className="text-zinc-500">Created</span>
                                <span className="text-zinc-400 font-mono text-[10px]">{(deal.created_at || "").slice(0, 16) || "—"}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-zinc-500 uppercase tracking-wider mb-1">Attribution</div>
                        <div className="grid grid-cols-[100px_1fr] gap-y-0.5 text-[11px]">
                          <span className="text-zinc-500">UTM source</span><span className="text-zinc-300">{row.utm_source || "—"}</span>
                          <span className="text-zinc-500">UTM medium</span><span className="text-zinc-300">{row.utm_medium || "—"}</span>
                          <span className="text-zinc-500">UTM content</span><span className="text-zinc-300">{row.utm_content || "—"}</span>
                          <span className="text-zinc-500">UTM term</span><span className="text-zinc-300">{row.utm_term || "—"}</span>
                          <span className="text-zinc-500">fbclid</span><span className="text-zinc-300 truncate">{row.fbclid || "—"}</span>
                          <span className="text-zinc-500">gclid</span><span className="text-zinc-300 truncate">{row.gclid || "—"}</span>
                          <span className="text-zinc-500">ad_source</span><span className="text-zinc-300">{row.ad_source || "—"}</span>
                          <span className="text-zinc-500">referrer</span><span className="text-zinc-300 truncate" title={row.referrer}>{truncate(row.referrer, 50) || "—"}</span>
                          <span className="text-zinc-500">IP</span><span className="text-zinc-300 font-mono">{row.ip || "—"}</span>
                          <span className="text-zinc-500">session</span><span className="text-zinc-300 font-mono text-[10px]">{truncate(row.session, 22) || "—"}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-zinc-500 uppercase tracking-wider mb-1">Form / payload</div>
                        <div className="grid grid-cols-[100px_1fr] gap-y-0.5 text-[11px]">
                          {row.extracted_name && (<><span className="text-zinc-500">Name</span><span className="text-zinc-200 font-medium">{row.extracted_name}</span></>)}
                          {row.extracted_phone && (<><span className="text-zinc-500">Phone</span><span className="text-zinc-200">{row.extracted_phone}</span></>)}
                          {row.extracted_email && (<><span className="text-zinc-500">Email</span><span className="text-zinc-200">{row.extracted_email}</span></>)}
                          {row.extracted_company && (<><span className="text-zinc-500">Company</span><span className="text-zinc-200">{row.extracted_company}</span></>)}
                          {row.extracted_project_size && (<><span className="text-zinc-500">Project size</span><span className="text-zinc-200">{row.extracted_project_size}</span></>)}
                          {row.extracted_book_date && (<><span className="text-zinc-500">Meeting</span><span className="text-emerald-300">{row.extracted_book_date} {row.extracted_book_time}</span></>)}
                        </div>
                        {row.raw && (
                          <details className="mt-2">
                            <summary className="text-zinc-500 cursor-pointer hover:text-zinc-300 select-none text-[11px]">Raw payload</summary>
                            <pre className="mt-1 text-[10px] text-zinc-400 bg-zinc-950/80 border border-zinc-800 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(row.raw, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {submissions && submissions.total > limit && (
            <div className="flex items-center justify-between text-xs text-zinc-400">
              <span>Showing {offset + 1}–{Math.min(offset + (submissions.returned || 0), submissions.total)} of {fmtNum(submissions.total)}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="border-zinc-700 h-7 px-3">Previous</Button>
                <Button size="sm" variant="outline" disabled={offset + limit >= submissions.total}
                  onClick={() => setOffset(offset + limit)}
                  className="border-zinc-700 h-7 px-3">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
